// src/components/finance/FinanceProfitLoss.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchFinanceProfitLoss, exportFinance } from '../../api.js'
import FinanceNav from './FinanceNav.jsx'

function isoDate(d) {
  const dt = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toISOString().slice(0, 10)
}
function fmtDateFR(d) {
  if (!d) return '—'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleDateString('fr-FR')
}
function fmtMoney(v) {
  const n = Number(v || 0)
  return `${n.toLocaleString('fr-FR')} XOF`
}

export default function FinanceProfitLoss() {
  const defaultRange = useMemo(() => {
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), 1)
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return { from: isoDate(from), to: isoDate(to) }
  }, [])

  const [from, setFrom] = useState(defaultRange.from)
  const [to, setTo] = useState(defaultRange.to)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const res = await fetchFinanceProfitLoss({ from, to })
      setData(res)
    } catch (e) {
      setErr(String(e?.message || e || 'Erreur P&L'))
    } finally {
      setLoading(false)
    }
  }, [from, to])

  useEffect(() => { load() }, [load])

  return (
    <div className="p-6 space-y-6 min-h-screen">
      <div className="flex flex-wrap gap-3 items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Compte de résultat</h1>
          <div className="text-sm text-slate-500 mt-1">
            Période: <span className="font-semibold">{fmtDateFR(from)}</span> → <span className="font-semibold">{fmtDateFR(to)}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => exportFinance({ from, to, type: 'profit-loss', format: 'csv' })}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            disabled={loading}
          >
            🧾 Export CSV
          </button>
          <button
            type="button"
            onClick={() => exportFinance({ from, to, type: 'profit-loss', format: 'pdf' })}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-950 disabled:opacity-50"
            disabled={loading}
          >
            🖨️ Export PDF
          </button>
        </div>
      </div>

      <FinanceNav />

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Du</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Au</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2" />
          </div>
          <div>
            <button type="button" className="px-4 py-2 rounded-lg bg-slate-900 text-white w-full" onClick={load}>🔄</button>
          </div>
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-500 font-semibold">Produits</div>
          <div className="text-xl font-bold text-slate-900">{fmtMoney(data?.income)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-500 font-semibold">Charges</div>
          <div className="text-xl font-bold text-slate-900">{fmtMoney(data?.expense)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-500 font-semibold">Résultat net</div>
          <div className={`text-xl font-bold ${Number(data?.net) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            {fmtMoney(data?.net)}
          </div>
        </div>
      </div>
    </div>
  )
}
